import { apiClient } from '@/lib/apiClient';

const BASE = '/api/nfse';

export interface NfseItemInput {
  servicoId:        number;
  descricaoServico: string;
  codigoLC?:        string;
  unidade?:         string;
  isTributavel:     boolean;
  quantidade:       number;
  valorUnitario:    number;
  desconto:         number;
  aliquotaIss:      number;
}

export interface NfsePagamentoInput {
  formaPagamento: string;
  nomePagamento?: string;
  valor:          number;
}

export interface CriarNfseRequest {
  empresaId:                  number;
  rps:                        number;
  serie?:                     string;
  dtEmissao:                  string;
  dtSaida:                    string;
  naturezaOperacaoId:         number;
  naturezaOperacao?:          string;
  isAmbienteProducao:         boolean;
  clienteId:                  number;
  nomeRazao?:                 string;
  cnpjCpf?:                   string;
  inscricaoMunicipal?:        string;
  inscricaoEstadual?:         string;
  email?:                     string;
  telefone?:                  string;
  endereco?:                  string;
  numero?:                    string;
  complemento?:               string;
  bairro?:                    string;
  cidade?:                    string;
  uf?:                        string;
  cep?:                       string;
  cidadeIbge:                 number;
  itemListaServico?:          string;
  codigoTributacaoMunicipal?: string;
  codigoObra?:                string;
  codigoArt?:                 string;
  incentivadorCultural:       boolean;
  optanteSimplesNacional:     boolean;
  aliquotaIss:                number;
  valorDeducoes:              number;
  valorDescontoCondicionado:  number;
  valorDescontoIncondicionado:number;
  perRetPis:                  number;
  perRetCofins:               number;
  perRetInss:                 number;
  perRetIr:                   number;
  perRetCsll:                 number;
  perRetIssqn:                number;
  descricaoServico?:          string;
  pedido?:                    string;
  itens:                      NfseItemInput[];
  formasPagamento:            NfsePagamentoInput[];
}

export interface NfseResultado {
  sucesso:    boolean;
  status:     string;
  motivo:     string;
  numeroNfse: string | null;
  protocolo:  string | null;
  chaveNota:  string | null;
  notaId:     number;
}

export interface NfseListItem {
  id:          number;
  operacaoId:  number;
  rps:         number;
  numeroNota:  number;
  serie:       string | null;
  dtEmissao:   string;
  nomeRazao:   string | null;
  cnpjCpf:     string | null;
  valorNota:   number;
  statusErro:  string | null;
  isCancelada: number;
  sChaveNota:  string | null;
}

/** Filtros server-side de GET /api/nfse. */
export interface ListaNfseFiltros {
  busca?: string;
  /** autorizada | pendente | aguardando | cancelada ('todos' = sem filtro). */
  situacao?: string;
  dtInicio?: string;
  dtFim?: string;
  /** Aliases de dtInicio/dtFim (a API aceita os dois nomes). */
  inicio?: string;
  fim?: string;
  ordenarPor?: 'emissao' | 'numero' | 'rps';
  decrescente?: boolean;
}

/** Mesmo contrato de ListaNfseFiltros (nome usado pelas telas da paridade). */
export type FiltrosNfse = ListaNfseFiltros;

export interface NfseListResponse {
  total:   number;
  pagina:  number;
  tamanho: number;
  items:   NfseListItem[];
}

export interface NfseItemDetalhe {
  item:             number;
  servicoId:        number;
  descricaoServico: string | null;
  codigoLC:         string | null;
  unidade:          string | null;
  isTributavel:     boolean;
  quantidade:       number;
  valorUnitario:    number;
  valorTotalBruto:  number;
  valorTotalLiquido:number;
  aliquotaIss:      number;
  valorIss:         number;
}

export interface NfsePagamentoDetalhe {
  formaPagRec: string | null;
  valor:       number;
}

export interface NfseDetalhe {
  id:                         number;
  operacaoId:                 number;
  rps:                        number;
  numeroNota:                 number;
  serie:                      string | null;
  dtEmissao:                  string;
  dtSaida:                    string;
  naturezaOperacaoId:         number;
  naturezaOperacao:           string | null;
  isAmbienteProducao:         boolean;
  clienteId:                  number;
  nomeRazao:                  string | null;
  cnpjCpf:                    string | null;
  inscricaoMunicipal:         string | null;
  inscricaoEstadual:          string | null;
  email:                      string | null;
  telefone:                   string | null;
  endereco:                   string | null;
  numero:                     string | null;
  complemento:                string | null;
  bairro:                     string | null;
  cidade:                     string | null;
  uf:                         string | null;
  cep:                        string | null;
  cidadeIbge:                 number;
  itemListaServico:           string | null;
  codigoTributacaoMunicipal:  string | null;
  codigoObra:                 string | null;
  codigoArt:                  string | null;
  incentivadorCultural:       boolean;
  optanteSimplesNacional:     boolean;
  aliquotaIss:                number;
  valorServico:               number;
  valorDeducoes:              number;
  valorIss:                   number;
  valorIssRetido:             number;
  valorDescontoCondicionado:  number;
  valorDescontoIncondicionado:number;
  perRetPis:                  number;
  valorRetPis:                number;
  perRetCofins:               number;
  valorRetCofins:             number;
  perRetInss:                 number;
  valorRetInss:               number;
  perRetIr:                   number;
  valorRetIr:                 number;
  perRetCsll:                 number;
  valorRetCsll:               number;
  valorNota:                  number;
  descricaoServico:           string | null;
  pedido:                     string | null;
  sChaveNota:                 string | null;
  sProtocolo:                 string | null;
  sCodigoVerificacao:         string | null;
  statusErro:                 string | null;
  isCancelada:                number;
  itens:                      NfseItemDetalhe[];
  formasPagamento:            NfsePagamentoDetalhe[];
}

export const nfseService = {
  async criar(req: CriarNfseRequest): Promise<NfseResultado> {
    const { data } = await apiClient.post<NfseResultado>(BASE, req);
    return data;
  },

  /**
   * Lista paginada; busca (número/RPS, CNPJ/CPF ou nome do tomador), situação, período de
   * emissão (yyyy-MM-dd) e ordenação são feitos no servidor.
   */
  async listar(pagina = 1, tamanho = 30, filtros: ListaNfseFiltros = {}): Promise<NfseListResponse> {
    const { data } = await apiClient.get<NfseListResponse>(BASE, {
      params: {
        pagina, tamanho,
        busca: filtros.busca?.trim() || undefined,
        situacao: filtros.situacao && filtros.situacao !== 'todos' ? filtros.situacao : undefined,
        dtInicio: filtros.dtInicio || filtros.inicio || undefined,
        dtFim: filtros.dtFim || filtros.fim || undefined,
        ordenarPor: filtros.ordenarPor,
        decrescente: filtros.decrescente,
      },
    });
    return data;
  },

  async detalhe(id: number): Promise<NfseDetalhe> {
    const { data } = await apiClient.get<NfseDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async transmitir(id: number): Promise<NfseResultado> {
    const { data } = await apiClient.post<NfseResultado>(`${BASE}/${id}/transmitir`);
    return data;
  },

  async cancelar(id: number, motivo: string): Promise<NfseResultado> {
    const { data } = await apiClient.post<NfseResultado>(`${BASE}/${id}/cancelar`, { motivo });
    return data;
  },
};
